import React, { useState, useEffect } from 'react';
import { Shield, Building2, AlertTriangle, FlaskConical, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { useLocation } from 'react-router-dom';

const LOGO_URL = "/amidex-logo.svg";
// Storage key kept for backward compatibility with any previously-verified sessions.
const VERIFIED_KEY = 'amidex_age_verified';

// Trusted partner domains that bypass the credentialing gate for external checkout
const TRUSTED_PARTNER_DOMAINS = [
  'bioinfinity.co',
  'www.bioinfinity.co',
  'bioinfinity.com',
  'www.bioinfinity.com',
];

/**
 * Institutional Credentialing & Buyer Verification Gate.
 *
 * Compliant B2B access gate that must be passed before browsing the catalog.
 * Stores verification in localStorage to avoid repeated prompts.
 *
 * Bypasses for:
 * - External checkout from trusted partner domains
 */
const AgeGate = ({ children }) => {
  const [verified, setVerified] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const isExternalCheckout = location.pathname === '/checkout/external';

    if (isExternalCheckout) {
      setVerified(true);
      setLoading(false);
      return;
    }

    const isVerified = localStorage.getItem(VERIFIED_KEY);
    if (isVerified === 'true') {
      setVerified(true);
    } else {
      setVerified(false);
    }
    setLoading(false);
  }, [location]);

  const handleVerify = () => {
    localStorage.setItem(VERIFIED_KEY, 'true');
    setVerified(true);
  };

  const handleDecline = () => {
    window.location.href = 'https://www.google.com';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="w-10 h-10 border-4 border-slate-700 border-t-cyan-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (verified) {
    return children;
  }

  return (
    <div
      className="fixed inset-0 z-[9999] bg-slate-900 flex items-center justify-center p-4"
      data-testid="age-gate"
    >
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="relative max-w-lg w-full">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-8 py-6 text-center">
            <img
              src={LOGO_URL}
              alt="Amidex"
              className="h-10 w-auto mx-auto mb-4 brightness-0 invert"
            />
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full">
              <Building2 className="w-4 h-4 text-cyan-400" />
              <span className="text-xs font-medium text-cyan-400 uppercase tracking-wider">
                B2B Institutional Supply
              </span>
            </div>
          </div>

          <div className="px-8 py-8">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-slate-900 rounded-2xl flex items-center justify-center shadow-lg">
                <Building2 className="w-9 h-9 text-white" />
              </div>
            </div>

            <h1 className="text-2xl font-bold text-slate-900 text-center mb-3">
              Institutional Credentialing Access
            </h1>

            <p className="text-slate-600 text-center mb-6 text-sm leading-relaxed">
              Access to Amidex requires verified institutional credentials.
              Products are supplied strictly for laboratory analytical testing
              and industrial research applications.
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-800 text-xs leading-relaxed">
                    <strong>Notice:</strong> All materials listed are
                    technical-grade chemical reagents intended solely for
                    industrial laboratory processing, analytical testing, and
                    non-clinical research applications by qualified
                    institutional buyers. Products are not intended for
                    consumer use, diagnostic procedures, or therapeutic
                    application.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2 mb-8">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span>I represent a qualified institutional laboratory</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span>I am the authorized purchasing agent for my organization</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span>I understand these are technical-grade reagents for laboratory use only</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleVerify}
                className="flex-1 bg-slate-900 hover:bg-slate-800 text-white py-3 rounded-xl font-semibold text-sm"
                data-testid="age-verify-button"
              >
                Access Institutional Portal
              </Button>
              <Button
                onClick={handleDecline}
                variant="outline"
                className="flex-1 border-slate-300 text-slate-600 hover:bg-slate-50 py-3 rounded-xl font-semibold text-sm"
                data-testid="age-decline-button"
              >
                Exit
              </Button>
            </div>
          </div>

          <div className="bg-slate-50 px-8 py-4 border-t border-slate-100">
            <div className="flex items-center justify-center gap-4 text-xs text-slate-500">
              <div className="flex items-center gap-1.5">
                <FlaskConical className="w-3.5 h-3.5 text-slate-400" />
                <span>Analytical Grade</span>
              </div>
              <div className="w-1 h-1 bg-slate-300 rounded-full" />
              <div className="flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5 text-slate-400" />
                <span>High Assay Standard</span>
              </div>
              <div className="w-1 h-1 bg-slate-300 rounded-full" />
              <div className="flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5 text-slate-400" />
                <span>B2B Only</span>
              </div>
            </div>
          </div>
        </div>

        <p className="text-center text-slate-500 text-xs mt-6">
          © 2026 Amidex. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default AgeGate;
